import { Picker } from '@react-native-picker/picker'
import clsx from 'clsx'
import { useState, type ReactElement } from 'react'
import { View } from 'react-native'

import { ICON } from '@/src/general/enums/icons'

import { Icon } from '../media/Icon'
import { Typography } from '../text/Typography'
import { selectInputVariants } from './select-input-variants'
import type { TSelectInputProps } from './SelectInput.types'

const ASTERISK = '*'

export const SelectInput = <Value extends string | number = string | number>({
  radius,
  isDisabled,
  color,
  size,
  label: textLabel,
  isRequired,
  testID,
  items,
  placeholderItem,
  description,
  errorMessage,
  onValueChange,
  value,
}: TSelectInputProps<Value>): ReactElement => {
  const [isFocused, setIsFocused] = useState(false)

  const { inputWrapper, label, placeholder, dropdownIcon } = selectInputVariants({
    radius,
    isDisabled,
    isFocused,
    color,
    size,
  })

  return (
    <View className="w-full">
      {!!textLabel && (
        <Typography bold className={label()}>
          {textLabel}{' '}
          {!!isRequired && (
            <Typography ariaRole="span" colors="danger">
              {ASTERISK}
            </Typography>
          )}
        </Typography>
      )}
      <View className="relative">
        <Picker<Value>
          aria-disabled={isDisabled}
          enabled={!isDisabled}
          className={clsx(inputWrapper(), 'no-dropdown-icon', value === placeholderItem?.value && placeholder())}
          onBlur={() => setIsFocused(false)}
          onFocus={() => setIsFocused(true)}
          testID={testID}
          selectedValue={value}
          onValueChange={onValueChange}
        >
          {!!placeholderItem && <Picker.Item label={placeholderItem.label} value={placeholderItem.value} />}
          {items.map(({ label: itemLabel, value: itemValue }) => (
            <Picker.Item key={itemValue} label={itemLabel} value={itemValue} />
          ))}
        </Picker>
        <View className={clsx('pointer-events-none absolute right-0 mr-4 flex h-full justify-center', dropdownIcon())}>
          <Icon name={ICON.CHEVRON_DOWN} size={size} />
        </View>
      </View>
      <View className="pt-1 web:grid">
        {description && (
          <Typography size="xs" colors="default" contrast="low">
            {description}
          </Typography>
        )}
        {errorMessage && (
          <Typography size="xs" colors="danger" contrast="default">
            {errorMessage}
          </Typography>
        )}
      </View>
    </View>
  )
}
